// frontend/src/components/AppSections.js
import React from 'react';
import DataTable from './DataTable';
import HistoryPanel from './HistoryPanel';
import {
  ButtonGroup,
  Card,
  DeviceOption,
  DocumentTypeCard,
  LoadingOverlay,
  SelectBox,
  TransformationControls,
} from './AppCommon';
import { exportToExcel } from '../utils/excelExporter';
import {
  handleDirectorySelect,
  handleFileChange,
  getLoadingMessage,
} from '../modules/carHandlers';
import { DEVICE_TYPES, handleDeviceTypeSelect } from '../modules/deviceHandlers';
import { DOCUMENT_TYPE, STEP, UPLOAD_MODE } from '../modules/workflowConfig';
import {
  buildExportFileName,
  buildInfoItems,
  buildProcessButtonText,
  buildResultSummary,
  buildUploadTitle,
} from '../modules/viewModelBuilders';
import { canUseOneClickProcessing } from '../modules/oneClickValueProcessor';

const renderHistoryPanel = ({
  documentType,
  historyLoading,
  historyTasks,
  isDeviceMode,
  onLoad,
  onView,
}) => {
  const historyTitle = isDeviceMode
    ? '设备校准历史记录'
    : documentType === DOCUMENT_TYPE.ENTERPRISE
      ? '企业标准历史记录'
      : '国家/国际标准历史记录';

  const historyEmptyText = isDeviceMode
    ? '暂无设备校准历史记录，点击“查询历史记录”后加载。'
    : documentType === DOCUMENT_TYPE.ENTERPRISE
      ? '暂无企业标准历史记录，点击“查询历史记录”后加载。'
      : '暂无国家/国际标准历史记录，点击“查询历史记录”后加载。';

  return (
    <HistoryPanel
      title={historyTitle}
      emptyText={historyEmptyText}
      historyLoading={historyLoading}
      historyTasks={historyTasks}
      onLoad={onLoad}
      onView={onView}
    />
  );
};

const renderSelectDocumentType = ({
  isDeviceMode,
  isEnterpriseMode,
  isStandardMode,
  onSelectDocumentType,
}) => (
  <div className="section document-type-selection">
    <Card>
      <h3>选择文档类型</h3>
      <p className="section-desc">请选择需要处理的文档类型，系统将自动匹配处理流程：</p>
      <div className="document-type-grid">
        <DocumentTypeCard type={DOCUMENT_TYPE.STANDARD} title="国家/国际标准" desc="支持全量文档上传和单页上传两种模式" selected={isStandardMode} onSelect={onSelectDocumentType} />
        <DocumentTypeCard type={DOCUMENT_TYPE.ENTERPRISE} title="企业标准" desc="支持全量文档上传和单页上传两种模式" selected={isEnterpriseMode} onSelect={onSelectDocumentType} />
        <DocumentTypeCard type={DOCUMENT_TYPE.DEVICE} title="设备校准证书" desc="自动应用专业校准规则，解析多方向校准数据" selected={isDeviceMode} onSelect={onSelectDocumentType} />
      </div>
    </Card>
  </div>
);

const renderSelectDeviceType = ({
  documentType,
  historyLoading,
  historyTasks,
  isDeviceMode,
  onLoadHistory,
  onResetToTypeSelect,
  onViewHistory,
  selectedDeviceType,
  updateState,
}) => (
  <div className="section device-selection">
    <Card>
      <h3>选择设备类型</h3>
      <div className="device-grid">
        {DEVICE_TYPES.map((device) => (
          <DeviceOption
            key={device.value}
            device={device}
            selected={selectedDeviceType === device.value}
            onSelect={(type) => handleDeviceTypeSelect(type, (deviceType) => updateState({ deviceType }), (step) => updateState({ currentStep: step }))}
          />
        ))}
      </div>
      <ButtonGroup>
        <button className="btn secondary" onClick={onResetToTypeSelect}>← 返回</button>
      </ButtonGroup>
      {renderHistoryPanel({
        documentType,
        historyLoading,
        historyTasks,
        isDeviceMode,
        onLoad: onLoadHistory,
        onView: onViewHistory,
      })}
    </Card>
  </div>
);

const renderUpload = ({
  currentStep,
  deviceType,
  documentType,
  deviceFiles,
  file,
  historyLoading,
  historyTasks,
  isCarMode,
  isDeviceMode,
  isSingleCarMode,
  loading,
  onGoBackFromUpload,
  onLoadHistory,
  onProcessUpload,
  onViewHistory,
  selectedSinglePageDirectory,
  singlePageDirectoryOptions,
  state,
  updateState,
}) => (
  <div className="section">
    <Card>
      <h3>{buildUploadTitle(state, DEVICE_TYPES)}</h3>
      {isCarMode && (
        <div className="upload-mode-selector">
          <div className="mode-option">
            <input type="radio" id="mode-full" name="upload-mode" checked={state.uploadMode === UPLOAD_MODE.FULL} onChange={() => updateState({ uploadMode: UPLOAD_MODE.FULL, selectedSinglePageDirectory: '' })} />
            <label htmlFor="mode-full" className="mode-label">全量上传</label>
            <p className="mode-desc">上传完整 PDF，自动提取目录和表格</p>
          </div>
          <div className="mode-option">
            <input type="radio" id="mode-single" name="upload-mode" checked={state.uploadMode === UPLOAD_MODE.SINGLE} onChange={() => updateState({ uploadMode: UPLOAD_MODE.SINGLE })} />
            <label htmlFor="mode-single" className="mode-label">单页上传</label>
            <p className="mode-desc">上传单页图片/PDF，快速处理特定项目</p>
          </div>
        </div>
      )}

      {isSingleCarMode && (
        <div className="single-page-config">
          <SelectBox
            label="选择测试项目"
            value={selectedSinglePageDirectory}
            options={singlePageDirectoryOptions}
            onChange={(e) => updateState({ selectedSinglePageDirectory: e.target.value })}
          />
        </div>
      )}

      <div className="upload-area">
        <input
          id="file-input"
          type="file"
          className="hidden-input"
          accept={isSingleCarMode ? '.jpg,.jpeg,.png,.pdf,.doc,.docx' : '.pdf,.doc,.docx'}
          multiple={isDeviceMode}
          onChange={(e) => {
            if (isDeviceMode) {
              const selectedFiles = Array.from(e.target.files || []);
              updateState({ file: selectedFiles[0] || null, deviceFiles: selectedFiles, error: '' });
              return;
            }
            handleFileChange(e, (nextFile) => updateState({ file: nextFile, error: '' }), (error) => updateState({ error }));
          }}
        />
        <label htmlFor="file-input" className="upload-label">
          <div className="upload-main">
            {isDeviceMode
              ? ((deviceFiles?.length || 0) > 0 ? `已选择 ${deviceFiles.length} 个文件` : '点击选择一个或多个PDF文件至此')
              : (file ? file.name : (isSingleCarMode ? '点击选择单页图片/PDF至此' : '点击选择PDF文件至此'))}
          </div>
          <div className="upload-sub">{isSingleCarMode ? '支持 JPG/PNG/PDF' : (isDeviceMode ? '支持多文件 PDF' : '支持 PDF')}</div>
        </label>
      </div>
      {isDeviceMode && (deviceFiles?.length || 0) > 0 && (
        <div className="upload-sub" style={{ marginTop: '8px' }}>
          {deviceFiles.map((item) => item.name).join('；')}
        </div>
      )}
      <ButtonGroup>
        <button className="btn secondary" onClick={onGoBackFromUpload}>← 返回</button>
        {(file || (isDeviceMode && (deviceFiles?.length || 0) > 0)) && (
          <button
            className="btn primary"
            onClick={onProcessUpload}
            disabled={loading || (isSingleCarMode && !selectedSinglePageDirectory)}
          >
            {buildProcessButtonText(state)}
          </button>
        )}
      </ButtonGroup>

      {renderHistoryPanel({
        documentType,
        historyLoading,
        historyTasks,
        isDeviceMode,
        onLoad: onLoadHistory,
        onView: onViewHistory,
      })}
      {loading && <LoadingOverlay message={getLoadingMessage(documentType, deviceType, currentStep)} />}
    </Card>
  </div>
);

const renderSelectDirectory = ({
  availableTables,
  directoryInfo,
  loading,
  onExtractFromDirectory,
  onUpdateState,
  selectedDirectory,
  setError,
  setSelectedDirectory,
  setTableHtml
}) => (
  <div className="section directory-selection">
    <Card>
      <h3>选择测试项目</h3>
      <SelectBox
        label="测试项目"
        value={selectedDirectory}
        options={directoryInfo}
        onChange={(e) => handleDirectorySelect(e, setSelectedDirectory, availableTables, setTableHtml, setError)}
      />
      <ButtonGroup>
        <button className="btn secondary" onClick={() => onUpdateState({ file: null, currentStep: STEP.UPLOAD })}>← 重新选择文件</button>
        <button className="btn primary" onClick={onExtractFromDirectory} disabled={loading || !selectedDirectory}>
          {loading ? '提取中...' : '提取表格数据 →'}
        </button>
      </ButtonGroup>
    </Card>
  </div>
);

const renderConfig = ({
  availableLevels,
  currentStep,
  deviceType,
  documentType,
  historyLoading,
  historyTasks,
  isDeviceMode,
  isSingleCarMode,
  loading,
  onConfirmConfig,
  onLoadHistory,
  onViewHistory,
  selectedDirectory,
  selectedLevel,
  setSelectedLevel,
  shouldShowLevelSelector,
  tableHtml,
}) => (
  <div className="section config-section">
    <Card>
      <h3>配置提取参数</h3>
      <div className="config-summary">
        <p><strong>已选择：</strong><span className="highlight">{selectedDirectory}</span></p>
        <p><strong>文档类型：</strong><span className="highlight">{isSingleCarMode ? '车辆EMC单页数据' : '车辆EMC测试报告'}</span></p>
      </div>
      <div className="config-options">
        {shouldShowLevelSelector && <SelectBox label="等级" value={selectedLevel} options={availableLevels.map((level) => ({ label: level, value: level }))} onChange={(e) => setSelectedLevel(e.target.value)} />}
      </div>
      <ButtonGroup>
        <button className="btn secondary" onClick={() => onConfirmConfig('back')}>← 返回</button>
        <button className="btn primary" onClick={() => onConfirmConfig('submit')} disabled={loading || !tableHtml}>
          {loading ? '提取中...' : '提取表格数据 →'}
        </button>
      </ButtonGroup>
      {renderHistoryPanel({
        documentType,
        historyLoading,
        historyTasks,
        isDeviceMode,
        onLoad: onLoadHistory,
        onView: onViewHistory,
      })}
      {loading && <LoadingOverlay message={getLoadingMessage(documentType, deviceType, currentStep)} />}
    </Card>
  </div>
);

const renderResults = ({
  availableLevels,
  deviceInfo,
  documentType,
  deviceType,
  isDeviceMode,
  onApplyAbsolute,
  onApplyDecrease,
  onApplyIncrease,
  onApplyNegative,
  onApplyOpposite,
  onCellValueChange,
  onLoadHistory,
  onOffsetAmountChange,
  onResetProcessing,
  onResetTransformation,
  onViewHistory,
  selectedLevel,
  setSelectedLevel,
  shouldShowLevelSelector,
  state,
}) => {
  const infoItems = buildInfoItems(state, deviceInfo);

  return (
    <div className="section results-section">
      <Card>
        <div className="results-header">
          <h3>处理结果</h3>
          <div className="result-summary">{buildResultSummary(state, deviceInfo)}</div>
        </div>
        <div className="info-summary">
          <h4 className="info-title">{isDeviceMode ? '设备校准信息' : '提取配置信息'}</h4>
          <ul>
            {infoItems.map((item) => (
              <li key={item.label}><strong>{item.label}：</strong>{item.value}</li>
            ))}
          </ul>
        </div>

        {shouldShowLevelSelector && (
          <div className="config-options">
            <SelectBox label="切换等级" value={selectedLevel} options={availableLevels.map((level) => ({ label: level, value: level }))} onChange={(e) => setSelectedLevel(e.target.value)} />
          </div>
        )}

        {canUseOneClickProcessing(documentType) && state.tables.length > 0 && (
          <TransformationControls
            currentTransformationType={state.currentTransformationType}
            offsetAmount={state.offsetAmount}
            onOffsetAmountChange={onOffsetAmountChange}
            onApplyOpposite={onApplyOpposite}
            onApplyAbsolute={onApplyAbsolute}
            onApplyNegative={onApplyNegative}
            onApplyIncrease={onApplyIncrease}
            onApplyDecrease={onApplyDecrease}
            onReset={onResetTransformation}
          />
        )}

        <DataTable
          tables={state.transformedTables}
          documentType={documentType}
          deviceType={deviceType}
          enableCellEdit={canUseOneClickProcessing(documentType)}
          onCellValueChange={onCellValueChange}
        />

        <div className="action-buttons">
          <button className="btn secondary" onClick={onResetProcessing}>← 重新处理</button>
          <button
            className="btn primary"
            onClick={() => exportToExcel(
              state.transformedTables,
              buildExportFileName(state, DEVICE_TYPES),
              isDeviceMode ? '' : selectedLevel,
              '',
              {
                documentType,
                deviceType,
              }
            )}
            disabled={state.loading || state.tables.length === 0}
          >
            保存Excel
          </button>
        </div>
        {renderHistoryPanel({
          documentType,
          historyLoading: state.historyLoading,
          historyTasks: state.historyTasks,
          isDeviceMode,
          onLoad: onLoadHistory,
          onView: onViewHistory,
        })}
      </Card>
    </div>
  );
};

export const renderAppStep = (params) => {
  switch (params.currentStep) {
    case STEP.SELECT_TYPE:
      return renderSelectDocumentType(params);
    case STEP.SELECT_DEVICE:
      return renderSelectDeviceType(params);
    case STEP.UPLOAD:
      return renderUpload(params);
    case STEP.SELECT_DIRECTORY:
      return renderSelectDirectory(params);
    case STEP.CONFIG:
      return renderConfig(params);
    case STEP.RESULTS:
      return renderResults(params);
    default:
      return null;
  }
};